import React, { useEffect, useRef, useState } from 'react';
import Logo from './site/Logo';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Mail, Menu, Phone, X } from 'lucide-react';
import { BRAND, NAV, waLink } from './site/brand';
import './site/header.css';

const MENUS = [
  { key: 'international', label: 'International', items: NAV.international, wide: true },
  { key: 'india', label: 'India', items: NAV.india, wide: true },
  { key: 'themes', label: 'Holiday Styles', items: NAV.themes },
  { key: 'services', label: 'Services', items: NAV.services },
];

const Header = () => {
  const [open, setOpen] = useState(null); // desktop dropdown key
  const [mobile, setMobile] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => { setOpen(null); setMobile(false); setMobileSection(null); }, [location.pathname]);

  // Lock body scroll when the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobile ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobile]);

  const enter = (key) => { clearTimeout(closeTimer.current); setOpen(key); };
  const leave = () => { closeTimer.current = setTimeout(() => setOpen(null), 180); };

  return (
    <header className={`vk-header ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="vk-topbar">
        <div className="vk-wrap vk-topbar__inner">
          <div className="vk-topbar__left">
            <a href={BRAND.phoneLink} data-testid="header-topbar-mobile"><Phone size={14} /> {BRAND.phone}</a>
            <a href={BRAND.landlineLink} className="vk-hide-sm" data-testid="header-topbar-landline"><Phone size={14} /> {BRAND.landline}</a>
            <a href={`mailto:${BRAND.email}`} className="vk-hide-md"><Mail size={14} /> {BRAND.email}</a>
          </div>
          <p className="vk-topbar__offices">{BRAND.offices}</p>
        </div>
      </div>

      <div className="vk-nav">
        <div className="vk-wrap vk-nav__inner">
          <Link to="/" className="vk-nav__logo" aria-label="VK Tours home">
            <Logo width="160" height="48" />
          </Link>

          <nav className="vk-nav__menu" aria-label="Main">
            <Link to="/" className="vk-nav__link">Home</Link>
            {MENUS.map((m) => (
              <div key={m.key} className="vk-nav__item" onMouseEnter={() => enter(m.key)} onMouseLeave={leave}>
                <button
                  type="button"
                  className={`vk-nav__link ${open === m.key ? 'is-open' : ''}`}
                  aria-expanded={open === m.key}
                  onClick={() => setOpen(open === m.key ? null : m.key)}
                >
                  {m.label} <ChevronDown size={15} />
                </button>
                {open === m.key && (
                  <div className={`vk-drop ${m.wide ? 'vk-drop--wide' : ''}`} role="menu">
                    {m.items.map((it) => (
                      <Link key={it.path} to={it.path} className="vk-drop__link" role="menuitem">{it.name}</Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/tour-packages" className="vk-nav__link">All Packages</Link>
            <Link to="/about" className="vk-nav__link">About</Link>
            <Link to="/contact" className="vk-nav__link">Contact</Link>
          </nav>

          <div className="vk-nav__cta">
            <a href={waLink()} target="_blank" rel="noopener noreferrer" className="vk-btn vk-btn--hot vk-btn--sm">Get a quote</a>
          </div>

          <button type="button" className="vk-nav__burger" aria-label={mobile ? 'Close menu' : 'Open menu'} aria-expanded={mobile} onClick={() => setMobile(!mobile)}>
            {mobile ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="vk-mnav" role="dialog" aria-label="Menu">
          <div className="vk-mnav__body">
            <Link className="vk-mnav__head" to="/">Home</Link>
            {MENUS.map((m) => (
              <div key={m.key} className="vk-mnav__group">
                <button type="button" className="vk-mnav__head" aria-expanded={mobileSection === m.key} onClick={() => setMobileSection(mobileSection === m.key ? null : m.key)}>
                  {m.label} <ChevronDown size={18} className={mobileSection === m.key ? 'is-rot' : ''} />
                </button>
                {mobileSection === m.key && (
                  <div className="vk-mnav__links">
                    {m.items.map((it) => <Link key={it.path} to={it.path}>{it.name}</Link>)}
                  </div>
                )}
              </div>
            ))}
            <Link className="vk-mnav__head" to="/tour-packages">All Packages</Link>
            <Link className="vk-mnav__head" to="/about">About</Link>
            <Link className="vk-mnav__head" to="/contact">Contact</Link>
          </div>
          <div className="vk-mnav__foot">
            <a href={waLink()} target="_blank" rel="noopener noreferrer" className="vk-btn vk-btn--hot vk-btn--block">Get a free quote</a>
            <a href={BRAND.phoneLink} className="vk-btn vk-btn--outline vk-btn--block" data-testid="header-mobile-menu-mobile"><Phone size={18} /> {BRAND.phone}</a>
            <p className="vk-mnav__offices">{BRAND.offices}</p>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
